import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check, Mail, ShieldCheck, Building2, CalendarDays } from "lucide-react";
import { PageHero } from "@/components/Shared";
import { privacyMeta as M, privacySections } from "@/pages/privacyPolicy";

export function Block({ b }) {
  if (b.h4) return <h4 className="text-gold text-xs font-bold uppercase tracking-widest mt-6">{b.h4}</h4>;
  if (b.h) return <h3 className="font-display text-lg font-bold text-white mt-8 mb-3">{b.h}</h3>;
  if (b.p) return <p className="text-slate-300 leading-relaxed mt-3">{b.p}</p>;
  if (b.note) return <p className="mt-4 border-l-2 border-gold bg-gold/5 px-5 py-4 text-slate-200 leading-relaxed">{b.note}</p>;
  if (b.list) return (
    <ul className={`mt-4 grid gap-x-8 gap-y-2.5 ${b.cols ? "sm:grid-cols-2" : ""}`}>
      {b.list.map((li) => (
        <li key={li} className="flex gap-3 text-slate-300 text-[15px]"><Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" /><span>{li}</span></li>
      ))}
    </ul>
  );
  if (b.items) return (
    <div className={`mt-5 grid gap-4 ${b.grid ? "md:grid-cols-2" : ""}`}>
      {b.items.map((it) => (
        <div key={it.t} className="bg-navy-paper border border-slate-800 hover:border-gold/40 transition-colors p-5">
          <div className="text-gold text-xs font-bold uppercase tracking-widest">{it.t}</div>
          <p className="text-slate-300 text-sm leading-relaxed mt-2">{it.d}</p>
        </div>
      ))}
    </div>
  );
  if (b.contact) return (
    <div className="mt-5 bg-navy-paper border border-gold/30 p-6" data-testid="privacy-contact-card">
      <div className="text-gold text-xs uppercase tracking-[0.3em]">Privacy Officer</div>
      <div className="text-white font-display text-xl font-bold mt-2">{M.operator}</div>
      <a href={`mailto:${M.email}`} className="inline-flex items-center gap-2 text-gold hover:text-gold-light mt-3 underline" data-testid="privacy-contact-email"><Mail className="w-4 h-4" /> {M.email}</a>
    </div>
  );
  return null;
}

function Toc() {
  return (
    <nav className="sticky top-28 bg-navy-paper border border-slate-800 p-5 max-h-[calc(100vh-8rem)] overflow-y-auto" data-testid="privacy-toc">
      <div className="text-gold text-xs uppercase tracking-[0.3em] mb-4">Contents</div>
      <ol className="space-y-2 text-sm">
        {privacySections.map((s, i) => (
          <li key={s.id}><a href={`#${s.id}`} className="flex gap-2 text-slate-400 hover:text-gold transition-colors" data-testid={`privacy-toc-${s.id}`}><span className="text-gold/70 w-5 text-right">{i + 1}.</span>{s.title}</a></li>
        ))}
      </ol>
    </nav>
  );
}

export default function PrivacyPolicy() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 100);
    return () => clearTimeout(t);
  }, [hash]);
  const meta = [
    { icon: Building2, l: "Operated by", v: M.operator },
    { icon: ShieldCheck, l: "ABN", v: M.abn },
    { icon: CalendarDays, l: "Effective date", v: M.effective },
  ];
  return (
    <div data-testid="info-page-privacy">
      <PageHero title={M.title} subtitle={M.subtitle} testId="info-hero-privacy" />
      <div className="border-b border-white/10 bg-navy-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10" data-testid="privacy-meta">
          {meta.map((m) => (
            <div key={m.l} className="py-5 sm:px-6 flex items-center gap-3">
              <m.icon className="w-5 h-5 text-gold flex-shrink-0" />
              <div><div className="text-slate-500 text-[10px] uppercase tracking-widest">{m.l}</div><div className="text-white text-sm font-medium">{m.v}</div></div>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid lg:grid-cols-[260px_1fr] gap-12">
        <aside className="hidden lg:block"><Toc /></aside>
        <div className="max-w-3xl space-y-16">
          {privacySections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`privacy-section-${s.id}`}>
              <div className="flex items-baseline gap-4 border-b border-gold/20 pb-3">
                <span className="font-display text-3xl font-bold text-gold/60">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-gold">{s.title}</h2>
              </div>
              {s.blocks.map((b, j) => <Block key={j} b={b} />)}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
